import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Typography, alpha } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import type { HansAckerRecommendationPhase } from './types';
import { InlineText } from './Rich';

const PHASE_COLOR: Record<HansAckerRecommendationPhase['id'], string> = { immediate: '#ef4444', short: '#f59e0b', medium: '#3b82f6' };

export default function Recommendations({ phases }: { phases: HansAckerRecommendationPhase[] }) {
  const [open, setOpen] = useState<Set<string>>(new Set(['immediate']));
  const toggle = (id: string) => setOpen((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <Box id="hans-acker-roadmap" sx={{ scrollMarginTop: 72, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 2.5 }}>
      <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700 }}>Recommendations</Typography>
        <Typography sx={{ ml: 'auto', fontSize: 12, color: 'text.disabled' }}>Ordered by urgency</Typography>
      </Stack>
      <Stack spacing={1.25}>
        {phases.map((p) => {
          const color = PHASE_COLOR[p.id];
          return (
            <Accordion
              key={p.id}
              expanded={open.has(p.id)}
              onChange={() => toggle(p.id)}
              disableGutters
              elevation={0}
              sx={{ border: '1px solid', borderColor: 'divider', borderLeft: `3px solid ${color}`, borderRadius: '10px !important', '&:before': { display: 'none' }, bgcolor: 'transparent' }}
            >
              <AccordionSummary expandIcon={<ChevronDown size={16} />} sx={{ px: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Typography sx={{ fontSize: 13, fontWeight: 800, letterSpacing: 0.7, textTransform: 'uppercase', color }}>{p.title}</Typography>
                  <Box sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', bgcolor: alpha(color, 0.12), borderRadius: 3, px: 1.1, py: 0.2 }}>
                    {p.items.length} action{p.items.length > 1 ? 's' : ''}
                  </Box>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2 }}>
                <Box component="ol" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 1.1, '& li::marker': { color, fontWeight: 800 } }}>
                  {p.items.map((item) => (
                    <Typography key={item} component="li" sx={{ fontSize: 13, lineHeight: 1.55, pl: 0.5 }}><InlineText text={item} /></Typography>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>
    </Box>
  );
}
